/* =================================================================
   XENA FIND MY SIZE — Hub page companion script (v4)
   2026-09-17 — same-day revert per Dima:
     - REMOVED the modal step-relabeling patch entirely (patchIndicatorText,
       installHubStepObserver, patchModalStepsForHub, and the click-handler
       call into it). The quiz modal is back to its native, untouched
       3-step flow ("Step 1 of 3" / "Step 2 of 3" / "Step 3 of 3") so it
       matches its own "3 quick questions → we'll recommend your size"
       copy again. The gallery's separate "Step 1 of 4" indicator was also
       removed (see sections/find-my-size-hub.liquid) — there is no longer
       a combined step count spanning gallery + modal.
   2026-09-17 — round 3 optimization pass per Dima:
     - FIXED: identifyFromLink() (the silent ?e= Klaviyo identify) checked
       window.klaviyo/_learnq exactly once, synchronously, at page-load —
       the SAME class of load-order race that used to break the saved-sizes
       box. If Klaviyo's onsite script hadn't finished loading yet, the
       identify call silently never fired. Fixed with whenKlaviyoReady() —
       same polling pattern as whenFitFinderReady(), applied here too.
     - ADDED: a "Loading your saved sizes…" placeholder shown immediately
       (before core is confirmed ready) whenever at least one style already
       has a saved profile in localStorage, so the box never pops in empty
       and refills a moment later.
     - ADDED: focus is now restored to the gallery tile that was originally
       clicked when "Start Over" or "Select New Style" returns to the
       gallery (previously only the plain "×" close button — via core's own
       focus-trap logic — restored focus; the two hub-specific overrides
       bypass core's closeModal() entirely via a capture-phase intercept,
       so they never got that behavior for free).
     - ADDED: a hub-only "Fit Finder Hub Style Selected" / GA4
       fit_finder_hub_style_selected event fires the moment a gallery tile
       is clicked, mirroring core fit-finder.js's own
       fireKlaviyoEvent/fireGAEvent pattern exactly. Core's own
       Started/Step Completed/Completed events already fire once the modal
       opens, so this is the one genuinely new signal needed for full
       gallery-entry funnel visibility (which style someone picked first),
       not a duplicate of what core already tracks.
     - Option/width detection robustness (contains-based, case-insensitive
       wide match) and eager-loading the first 6 tiles now live in
       snippets/ff-hub-tile.liquid — nothing to change here for those.
     - Exclusion tag is now `exclude-find-my-size` OR the older
       `exclude-product` — this file doesn't reference the tag directly,
       so no change needed here; noted for context.
   2026-09-15 — round 2 fixes per Dima:
     - FIXED: "Your saved sizes" showed "—" instead of the real size on
       first page load. Root cause: this script is deferred and can
       execute (and therefore call refreshSavedList(), which needs
       window.XenaFitFinder.recommendSize from the core fit-finder.js)
       BEFORE core's own deferred script has run, depending on script
       order in the DOM. Fixed with whenFitFinderReady() — polls up to ~2s
       for window.XenaFitFinder before the FIRST render, instead of just
       failing silently into the "—" placeholder. Every later refresh
       (after any modal interaction) is unaffected, since the modal itself
       cannot open without core already loaded.
     - FIXED: available_sizes_m/w passed to recommendSize() were left as
       arrays of STRINGS (straight from JSON.parse of the data attribute)
       instead of numbers, unlike core's own readProductContext() which
       always maps them through parseFloat. This could cause an exact
       available size to miss a strict-equality lookup inside
       recommendSize() and get needlessly clamped. Now mapped through
       parseFloat here too, matching core exactly.
   ---
   Loads ONLY on templates/page.find-my-size.json via
   sections/find-my-size-hub.liquid. Adds hub-only behavior on top of
   the UNTOUCHED global assets/fit-finder.js + fit-finder-modal.liquid:
     - "Start Over" returns to the gallery instead of clearing + redoing
       the current style's quiz (a saved style's size is NOT cleared —
       intercepted in the CAPTURE phase so core's clearProfile() never runs).
     - "Select New Style" (not-available screen) returns to the gallery
       instead of redirecting to /collections/all-products (also
       capture-phase intercepted, for the same reason).
     - "Select Size" navigates to the chosen product's page instead of
       just closing the modal — the PDP's own autoApplyOnLoad() then shows
       the saved-size pill, because it reads the SAME localStorage key
       this page just wrote.
     - Checkmarks already-sized tiles + a "Your saved sizes" strip.
     - Silent Klaviyo identify from a ?e= link param, and pushes a
       xena_size_profile property (same shape as customer.xena.fit_profile)
       whenever a style's quiz completes.
   Does NOT modify fit-finder.js. Destructive hub-specific overrides use a
   capture-phase listener (runs before core's bubble-phase handler) so they
   can stop the default PDP-context behavior from running at all; everything
   else is a normal bubble-phase listener that runs AFTER core's handler.
   ================================================================= */
(function () {
  'use strict';

  var STORAGE_KEY_PREFIX = 'xena_fit_profile_';
  var hubLastFocusedTile = null;

  function getHandleFromTile(tile) {
    return tile && tile.dataset ? tile.dataset.ffProductHandle : null;
  }

  function activeTileHandle() {
    var ctx = window.XenaFitFinder && window.XenaFitFinder.state && window.XenaFitFinder.state.productContext;
    return ctx ? ctx.product_handle : null;
  }

  function closeModalDom() {
    var modal = document.getElementById('fit-finder-modal');
    if (!modal) return;
    modal.classList.add('fit-finder--hidden');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function scrollToGallery() {
    var gallery = document.getElementById('find-my-size-gallery');
    if (gallery) gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Restores focus to the gallery tile that opened the quiz, for the two
  // hub-specific return-to-gallery paths (Start Over / Select New Style)
  // that bypass core's closeModal() \u2014 and therefore its own lastFocused
  // restore \u2014 entirely via the capture-phase intercept below.
  function restoreHubFocus() {
    if (hubLastFocusedTile && typeof hubLastFocusedTile.focus === 'function') {
      setTimeout(function () { hubLastFocusedTile.focus(); }, 400);
    }
  }

  // ---- wait for core fit-finder.js to be ready (fixes the load-order race
  // that showed "—" instead of the real saved size on first page load) ----
  function whenFitFinderReady(cb, attempts) {
    attempts = attempts || 0;
    if (window.XenaFitFinder && typeof window.XenaFitFinder.recommendSize === 'function') { cb(); return; }
    if (attempts > 40) { cb(); return; } // give up gracefully after ~2s, still render (shows "—" only in this edge case)
    setTimeout(function () { whenFitFinderReady(cb, attempts + 1); }, 50);
  }

  // ---- wait for Klaviyo's onsite script to be ready (same race as above,
  // applied to the ?e= silent identify) ----
  function whenKlaviyoReady(cb, attempts) {
    attempts = attempts || 0;
    var ready = !!window._learnq || (window.klaviyo && typeof window.klaviyo.push === 'function');
    if (ready) { cb(); return; }
    if (attempts > 60) return; // give up silently after ~3s, matching core's own silent-fail posture
    setTimeout(function () { whenKlaviyoReady(cb, attempts + 1); }, 50);
  }

  // ---- "already sized" checkmarks ----
  function refreshCheckmarks() {
    document.querySelectorAll('[data-ff-hub-tile]').forEach(function (tile) {
      var handle = getHandleFromTile(tile);
      var has = false;
      try { has = !!localStorage.getItem(STORAGE_KEY_PREFIX + handle); } catch (e) {}
      tile.classList.toggle('is-sized', has);
    });
  }

  function hasAnySavedProfile() {
    var found = false;
    document.querySelectorAll('[data-ff-hub-tile]').forEach(function (tile) {
      if (found) return;
      var handle = getHandleFromTile(tile);
      try { if (localStorage.getItem(STORAGE_KEY_PREFIX + handle)) found = true; } catch (e) {}
    });
    return found;
  }

  // Shows a lightweight loading placeholder in the saved-sizes box the
  // instant we know (cheaply, via localStorage) that there's at least one
  // saved size \u2014 rather than leaving the box hidden/empty until core
  // finishes loading and refreshSavedList() can compute real sizes.
  function showSavedListLoadingState() {
    var wrap = document.querySelector('[data-ff-hub-saved]');
    var list = document.querySelector('[data-ff-hub-saved-list]');
    if (!wrap || !list) return;
    if (!hasAnySavedProfile()) return;
    wrap.hidden = false;
    list.classList.add('is-loading');
    list.innerHTML = '<p class="ff-hub__saved-loading-text">Loading your saved sizes\u2026</p>';
  }

  // ---- "Your saved sizes" strip ----
  function refreshSavedList() {
    var wrap = document.querySelector('[data-ff-hub-saved]');
    var list = document.querySelector('[data-ff-hub-saved-list]');
    if (!wrap || !list) return;
    list.classList.remove('is-loading');
    var rows = [];
    document.querySelectorAll('[data-ff-hub-tile]').forEach(function (tile) {
      var handle = getHandleFromTile(tile);
      var raw = null;
      try { raw = localStorage.getItem(STORAGE_KEY_PREFIX + handle); } catch (e) {}
      if (!raw) return;
      try {
        var profile = JSON.parse(raw);
        if (!profile || !profile.typical_size || !profile.width_pref || !profile.fit_pref) return;
        rows.push({ handle: handle, styleName: tile.dataset.ffStyleName || tile.dataset.ffProductTitle || handle, profile: profile, tile: tile });
      } catch (e) {}
    });
    list.innerHTML = '';
    rows.forEach(function (row) {
      var rec = (window.XenaFitFinder && window.XenaFitFinder.recommendSize) ? window.XenaFitFinder.recommendSize({
        typical_size: row.profile.typical_size,
        width_pref: row.profile.width_pref,
        fit_pref: row.profile.fit_pref,
        fit_type: row.tile.dataset.ffFitType,
        available_sizes_m: JSON.parse(row.tile.dataset.ffSizesM || '[]').map(parseFloat).filter(function (n) { return !isNaN(n); }),
        available_sizes_w: JSON.parse(row.tile.dataset.ffSizesW || '[]').map(parseFloat).filter(function (n) { return !isNaN(n); })
      }) : null;
      var sizeStr = rec ? (String(rec.size) + (rec.width === 'W' ? 'W' : '')) : '\u2014';
      var a = document.createElement('a');
      a.className = 'ff-hub__saved-item';
      a.href = '/products/' + row.handle;
      a.innerHTML = '<span class="ff-hub__saved-item-style">' + row.styleName + '</span>' +
        '<span class="ff-hub__saved-item-size">Size ' + sizeStr + '</span>';
      list.appendChild(a);
    });
    wrap.hidden = rows.length === 0;
  }

  function refreshAll() { refreshCheckmarks(); refreshSavedList(); }

  // ---- Klaviyo identify from link param (silent, per Dima's choice) ----
  function identifyFromLink() {
    var email;
    try {
      var params = new URLSearchParams(window.location.search);
      email = params.get('e');
    } catch (e) { return; }
    if (!email) return;
    whenKlaviyoReady(function () {
      try {
        if (window._learnq) window._learnq.push(['identify', { '$email': email }]);
        else if (window.klaviyo && typeof window.klaviyo.push === 'function') window.klaviyo.push(['identify', { '$email': email }]);
      } catch (e) {}
    });
  }

  // ---- Push the full multi-style profile to Klaviyo (same shape as customer.xena.fit_profile) ----
  function buildFullProfile() {
    var map = {};
    document.querySelectorAll('[data-ff-hub-tile]').forEach(function (tile) {
      var handle = getHandleFromTile(tile);
      var raw = null;
      try { raw = localStorage.getItem(STORAGE_KEY_PREFIX + handle); } catch (e) {}
      if (!raw) return;
      try {
        var p = JSON.parse(raw);
        if (p && p.typical_size && p.width_pref && p.fit_pref) {
          map[handle] = { ts: p.typical_size, w: p.width_pref, f: p.fit_pref, u: p.updated_at };
        }
      } catch (e) {}
    });
    return map;
  }
  function pushProfileToKlaviyo() {
    if (!(window.klaviyo && typeof window.klaviyo.push === 'function')) return;
    var map = buildFullProfile();
    if (!Object.keys(map).length) return;
    window.klaviyo.push(['identify', { 'xena_size_profile': map }]);
  }

  // ---- hub-only funnel-entry telemetry (mirrors core fit-finder.js's own
  // fireKlaviyoEvent/fireGAEvent pattern exactly \u2014 core doesn't expose
  // those helpers globally, so this is a small local copy, not a duplicate
  // call into core). Fires once per tile click, distinct from core's own
  // Started/Step Completed/Completed events (which already fire once the
  // modal opens) \u2014 this is the one new signal: WHICH style someone
  // picked first on the gallery. ----
  function fireHubStyleSelected(tile) {
    var payload = {
      product_id: tile.dataset.ffProductId,
      product_handle: tile.dataset.ffProductHandle,
      style_name: tile.dataset.ffStyleName,
      tile_source: tile.dataset.ffHubTileSource
    };
    try {
      if (window._learnq) window._learnq.push(['track', 'Fit Finder Hub Style Selected', payload]);
      else if (window.klaviyo && typeof window.klaviyo.push === 'function') window.klaviyo.push(['track', 'Fit Finder Hub Style Selected', payload]);
    } catch (e) {}
    try {
      if (typeof gtag === 'function') gtag('event', 'fit_finder_hub_style_selected', payload);
      else if (window.dataLayer) window.dataLayer.push({ event: 'fit_finder_hub_style_selected', ff: payload });
    } catch (e) {}
  }

  function init() {
    identifyFromLink();
    refreshCheckmarks();
    showSavedListLoadingState();
    whenFitFinderReady(refreshSavedList);

    // CAPTURE phase — runs before core fit-finder.js's bubble-phase handler.
    // Only intercepts the two destructive/redirecting actions; everything
    // else (size/width/fit selection, select-size, close) passes through
    // untouched to the core script.
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-fit-finder-start-over]')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeModalDom();
        refreshAll();
        scrollToGallery();
        restoreHubFocus();
        return;
      }
      if (e.target.closest('[data-fit-finder-select-new-style]')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeModalDom();
        refreshAll();
        scrollToGallery();
        restoreHubFocus();
        return;
      }
    }, true);

    // Normal bubble phase — runs AFTER core's handler already did its work.
    document.addEventListener('click', function (e) {
      var tileEl = e.target.closest('[data-ff-hub-tile]');
      if (tileEl) {
        hubLastFocusedTile = tileEl;
        fireHubStyleSelected(tileEl);
      }
      if (e.target.closest('[data-fit-finder-close]') || e.target.closest('[data-fit-finder-select-new-size]')) {
        setTimeout(refreshAll, 50);
      }
      if (e.target.closest('[data-fit-finder-fit]')) {
        setTimeout(function () { refreshAll(); pushProfileToKlaviyo(); }, 300);
      }
      if (e.target.closest('[data-fit-finder-select-size]')) {
        var handle = activeTileHandle();
        setTimeout(function () {
          refreshAll();
          pushProfileToKlaviyo();
          if (handle) window.location.href = '/products/' + handle;
        }, 150);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();